import { Box, Dialog, DialogContent, IconButton, Grid, CircularProgress, Button } from '@mui/material';
import { BackgroundStyle, HeadingTitle } from "../../assets/style";
import { photoIcon, washIcon } from '../../assets/imgs';
import { useEffect, useState } from "react";
import MainContent from '../../components/MainContent';
import { latestPhotoPageName } from "../../components/PageName";
import { useLazyGetLatestPhotoListQuery } from "../../features/photo/photoSlice";
import RecordTableV2, { TableHeader } from "../../components/RecordTableV2";
import { RecordModel } from "../../models/RecordModel";
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { logOut } from '../../features/auth/authSlice';
import SignalIndicator from '../../components/SignalIndicator';
import PhotoSphereViewerComponent from '../../components/PhotoSphereViewerComponent';
import { usePostStationWashMutation } from '../../features/station/StationSlice';


const LatestPhotoRecord = () => {
    const [getLatestPhotoList, { isFetching }] = useLazyGetLatestPhotoListQuery();
    const [postStationWash, { isLoading: isWashLoading }] = usePostStationWashMutation()
    const [tableRows, setTableRows] = useState<any[]>([]);
    const [data, setData] = useState<RecordModel<any>>();
    const [selectedStation, setSelectedStation] = useState<string>('');
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isImageOpen, setIsImageOpen] = useState(false);
    const [selectedPerspectiveImage, setSelectedPerspectiveImage] = useState<string[] | null>(null);
    const [isPerspectiveImageOpen, setIsPerspectiveImageOpen] = useState(false);
    const [reFetch, setReFetch] = useState<any>(null)

    const [washState, setWashState] = useState
        <{
            isConfirmOpen: boolean, pendingWashId: number | null,
            isResultOpen: boolean, isWashSuccess: boolean,
            pendingNextWashTime: string | null
        }>
        ({
            isConfirmOpen: false, pendingWashId: null,
            isResultOpen: false, isWashSuccess: false,
            pendingNextWashTime: null
        });

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const getColumns = (): TableHeader[] => [
        {
            field: 'stationName', headerName: t("Location"), width: 300, isSortable: false,
            renderCell: (params: any) => (
                <div onClick={(e) => {
                    navigate(`/recordDetail?stationName=${params.value}`);
                }}
                >
                    {params.value}
                </div>
            )
        },
        { field: 'uploadDate', headerName: t("Date"), width: 200, isSortable: false },
        {
            field: "thumbnailUrl",
            headerName: t("360° Photo"),
            width: 150,
            renderCell: (params: any) => (
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: 2,
                            transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePhotoClick(params);
                    }}
                >
                    <img
                        src={`${params.value}?token=${localStorage.getItem('accessToken')}`}
                        alt="Thumbnail"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </Box>
            )
        },
        {
            field: "photoPerspectiveUrls",
            headerName: t("Perspective Photo"),
            width: 150,
            renderCell: (params: any) => (
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePerspectivePhotoClick(params.value);
                    }}
                >
                    <img src={photoIcon} alt='logo' />
                </Box>
            )
        },
        {
            field: "canWash",
            headerName: t("Schedule Wash"),
            width: 150,
            renderCell: (params: any) => (
                params.value === true ?
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            washState.pendingWashId = params.row.id;
                            washState.pendingNextWashTime = params.row.nextWashTime;
                            washState.isConfirmOpen = true;
                            setWashState({ ...washState })
                        }}
                    >
                        {
                            isWashLoading ?
                                <CircularProgress />
                                : <img src={washIcon} alt='logo' />
                        }
                    </Box>
                    : <></>
            )
        },
        ...(tableRows.some((row: any) => row.rubbishRating !== null) ? [{
            field: "rubbishRating",
            headerName: t("Rubbish Level"),
            width: 150,
            renderCell: (params: any) => (
                <Box>
                    <SignalIndicator signalLevel={params.value} size='medium' />
                </Box>
            )
        }] : []),
    ];


    const fetchDataForPage = async (pageNumber: any) => {
        // Fetch data for the specified page from the backend
        const data = await getLatestPhotoList(
            {
                page: pageNumber,
                sortBy: 'uploadDate',
                sortDirection: 'asc'
            }
        );

        if (data.status === 'fulfilled') {
            return data.data.records.map(mapData)
        }

        if (data.status === 'rejected') {
            alert(t('An error occurred. Please login again.'));
            dispatch(logOut());
            navigate('/');
        }

        // return [];
    };

    const handleClosePhoto = () => {
        setIsImageOpen(false);
        setSelectedImage(null);
    };

    const handleClosePerspectivePhoto = () => {
        setIsPerspectiveImageOpen(false);
        setSelectedPerspectiveImage(null);
    };

    const handlePhotoClick = (rowData: any) => {
        // console.log("Button clicked for row:", rowData);
        // Add your custom logic here
        setSelectedStation(rowData.row.stationName);
        setSelectedImage(rowData.row.photoUrl);
        setIsImageOpen(true);
    };

    const handlePerspectivePhotoClick = (imageUrl: string[]) => {
        //console.log("handlePerspectivePhotoClick:", imageUrl);
        // Add your custom logic here
        setSelectedPerspectiveImage(imageUrl);
        setIsPerspectiveImageOpen(true);
    };

    const handleConfirmWash = () => {
        if (washState.pendingWashId) {
            washState.isConfirmOpen = false;
            setWashState({ ...washState });
            postStationWash(washState.pendingWashId)
                .unwrap()
                .then(() => {
                    washState.isWashSuccess = true;
                    setReFetch({})
                })
                .catch(() => {
                    washState.isWashSuccess = false;
                })
                .finally(() => {
                    washState.isResultOpen = true;
                    washState.pendingWashId = null
                    washState.pendingNextWashTime = null
                    setWashState({ ...washState })
                });
        }
    };

    const handleCloseConfirm = () => {
        washState.isConfirmOpen = false;
        washState.pendingWashId = null;
        washState.pendingNextWashTime = null;
        setWashState({ ...washState })
    };

    const handleSearchClick = async () => {
        try {
            getLatestPhotoList({
                sortBy: 'uploadDate',
                sortDirection: 'asc'
            }).then(
                (res) => {
                    if (res.status === 'fulfilled') {
                        const tableRows = res?.data.records.map(mapData)
                        setTableRows(tableRows)
                        setData(res?.data)
                    }

                    if (res.status === 'rejected') {
                        alert(t('An error occurred. Please login again.'));
                        dispatch(logOut());
                        navigate('/');
                    }
                }
            )
        } catch (error) {

        }
    }
    useEffect(() => {
        // console.log("data", data)
    }, [data])

    useEffect(() => {
        handleSearchClick();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <MainContent type={latestPhotoPageName}>
            <Box sx={{ bgcolor: '#456296' }}>
                <HeadingTitle>{t("Latest Photo Record")}</HeadingTitle>
            </Box>
            <BackgroundStyle sx={{ zIndex: 0 }}>
                {/* 360 Photo Popup */}
                <Dialog
                    open={isImageOpen}
                    onClose={handleClosePhoto}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ padding: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={handleClosePhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            >
                            </IconButton>
                            {selectedImage && <PhotoSphereViewerComponent stationName={selectedStation} image={selectedImage} title='360 Viewer' />}
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Perspective Photo Popup */}
                <Dialog
                    open={isPerspectiveImageOpen}
                    onClose={handleClosePerspectivePhoto}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ padding: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={handleClosePerspectivePhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            >
                            </IconButton>
                            <Grid container spacing={0}>
                                {selectedPerspectiveImage && selectedPerspectiveImage.map((img, index) => (
                                    <Grid key={index} item xs={6}>
                                        <img
                                            src={`${img}?token=${localStorage.getItem('accessToken')}` || ''}
                                            alt="Full preview"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '80vh',
                                                display: 'block', // Remove inline gaps
                                                verticalAlign: 'top', // Fix baseline spacing
                                            }}
                                        />
                                    </Grid>
                                ))
                                }
                            </Grid>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Confirmation Dialog */}
                <Dialog
                    open={washState.isConfirmOpen}
                    onClose={handleCloseConfirm}
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    {
                        washState.isConfirmOpen &&
                        <DialogContent sx={{ textAlign: 'center', py: 3, px: 4 }}>
                            {washState.pendingNextWashTime && new Date() < new Date(washState.pendingNextWashTime) && (
                                <Box sx={{ mb: 2, color: 'warning.main', fontWeight: 'bold' }}>
                                    {t("A washing is already scheduled at approximately:")} {new Date(washState.pendingNextWashTime).toLocaleString(i18n.language)}
                                </Box>
                            )}
                            {
                                (!washState.pendingNextWashTime || new Date(washState.pendingNextWashTime) < new Date()) && (
                                    <>
                                        <Box sx={{ mb: 3 }}>{t("Confirm to schedule washing?")}</Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                            <Button variant="outlined" onClick={handleCloseConfirm}>{t("Cancel")}</Button>
                                            <Button variant="contained" onClick={handleConfirmWash}>{t("Confirm")}</Button>
                                        </Box>
                                    </>
                                )
                            }
                        </DialogContent>
                    }
                </Dialog>

                {/* Result Dialog */}
                <Dialog
                    open={washState.isResultOpen}
                    onClose={() => {
                        washState.isResultOpen = false;
                        setWashState({ ...washState })
                    }}
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ textAlign: 'center', py: 3, px: 4 }}>
                        <Box sx={{ mb: 3 }}>
                            {washState.isWashSuccess
                                ? t("Washing is scheduled.")
                                : t("Failed to schedule washing. Please contact system support.")}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button variant="contained" onClick={() => {
                                washState.isResultOpen = false;
                                setWashState({ ...washState })
                            }}>{t("OK")}</Button>
                        </Box>
                    </DialogContent>
                </Dialog>

                <RecordTableV2
                    columns={getColumns()}
                    rows={tableRows}
                    checkbox={false}
                    pages={data?.totalPages}
                    totalCount={data?.totalCount}
                    // onClick={() => { }}
                    updateTableRows={setTableRows}
                    fetchDataForPage={fetchDataForPage}
                    isFetching={isFetching}
                    reFetch={reFetch}
                ></RecordTableV2>
            </BackgroundStyle>
        </MainContent >
    );
};

const mapData = (item: any) => ({
    id: item?.id,
    stationId: item.stationId,
    stationName: item.stationName,
    district: item.district,
    area: item.area,
    photoUrl: item.photoUrl,
    photoPerspectiveUrls: item.photoPerspectiveUrls,
    rubbishRating: item.rubbishRating,
    //uploadDate: new Date(item?.uploadDate).toLocaleString('en-US'),
    uploadDate: new Date(item?.uploadDate).toLocaleDateString('en-US'), //Only Show Date
    thumbnailUrl: item.thumbnailUrl,
    canWash: item.canWash,
    nextWashTime: item.nextWashTime,
});

export default LatestPhotoRecord;